import { useCallback, useMemo, useEffect } from 'react';
import compact from 'lodash/compact';
import find from 'lodash/find';
import merge from 'lodash/merge';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import concat from 'lodash/concat';
import uniqBy from 'lodash/uniqBy';
import omit from 'lodash/omit';

import { useFinLifestyleTypes } from '../../../../../hooks/useFinLifestyleTypes';
import { usePreviousValue } from '../../../../../../../common/hooks/usePreviousValue';

import { LifestyleCache } from '../../../../../LifestyleCache';

import {
  CategoryID,
  FetchCategoriesScopes,
  FetchCategoriesSortTypes,
  FetchFinCategoriesFiltersLifestyleFilters
} from '../../../../../../categories/categoriesTypes';

import {
  FetchLifestylesFilters,
  FetchFinLifestyleCategoriesFilters,
  LifestyleClientID,
  FetchLifestylesFilterFields,
  ChangeLifestylesFiltersFunc
} from '../../../../../lifestylesTypes';

import { getLifestyleClientIdsCacheKeyPart } from '../../../../../utils/getLifestyleClientIdsCacheKeyPart';
import { transformLifestylesFiltersToFinCategoriesLifestyleFilters } from '../../../../../utils/transformLifestylesFiltersToFinCategoriesLifestyleFilters';

const defaultInitialFilters = {
  scope: { eq: FetchCategoriesScopes.LIFESTYLES_TYPES }
};

const initialSort = [FetchCategoriesSortTypes.ID_ASC];

interface LifestyleTypesDataItem {
  id: string;
  label: string;
}

interface useLifestylesTypesFilterProps {
  clientIds?: LifestyleClientID[];
  selectedIds: CategoryID[];
  name: string;
  lifestylesFilters: FetchLifestylesFilters;
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
}

function useLifestylesTypesFilter({
  clientIds,
  selectedIds = [],
  name,
  lifestylesFilters,
  changeLifestylesFilters
}: useLifestylesTypesFilterProps) {
  const cacheKey = isEmpty(clientIds)
    ? LifestyleCache.typesFilterCacheKey()
    : LifestyleCache.typesLibraryFilterCacheKey(
        getLifestyleClientIdsCacheKeyPart({ clientIds })
      );

  const typesByLifestyle: FetchFinCategoriesFiltersLifestyleFilters =
    transformLifestylesFiltersToFinCategoriesLifestyleFilters(
      omit(lifestylesFilters, [FetchLifestylesFilterFields.LIFESTYLE_TYPE_ID])
    );

  const previousTypesByLifestyle = usePreviousValue(typesByLifestyle);

  const defaultFilters: FetchFinLifestyleCategoriesFilters = merge(
    {},
    defaultInitialFilters,
    {
      typesByLifestyle
    }
  );

  const {
    lifestyleTypes,
    lifestyleTypesErrorMessage,
    lifestyleTypesFetched,
    lifestyleTypesIsPlaceholderData,
    lifestyleTypesFetchingNextPage,
    hasNextLifestyleTypesPage,
    lifestyleTypesFilterSearchValue,
    loadMoreLifestyleTypes,
    filterLifestyleTypes,
    changeLifestyleTypesFilters
  } = useFinLifestyleTypes({
    cacheKey,
    initialFilters: defaultFilters,
    initialSort
  });

  useEffect(() => {
    if (!isEqual(typesByLifestyle, previousTypesByLifestyle)) {
      filterLifestyleTypes(defaultFilters);
    }
  }, [
    defaultFilters,
    filterLifestyleTypes,
    previousTypesByLifestyle,
    typesByLifestyle
  ]);

  const {
    lifestyleTypes: lifestyleTypesSelected,
    lifestyleTypesFilters: lifestyleTypesSelectedFilters,
    changeLifestyleTypesFilters: changeLifestyleTypesFiltersSelected,
    lifestyleTypesErrorMessage: lifestylesTypesSelectedErrorMessage
  } = useFinLifestyleTypes({
    cacheKey: LifestyleCache.typesSelectedFilterCacheKey(),
    initialFilters: {
      ...defaultInitialFilters,
      id: { in: selectedIds }
    },
    initialSort,
    options: {
      enabled: !isEmpty(selectedIds),
      enabledPlaceholder: !isEmpty(selectedIds)
    }
  });

  useEffect(() => {
    if (
      isEmpty(selectedIds) &&
      isEmpty(lifestyleTypesSelectedFilters?.id?.in)
    ) {
      return;
    }

    if (!isEqual(selectedIds, lifestyleTypesSelectedFilters?.id?.in)) {
      changeLifestyleTypesFiltersSelected({
        id: { in: selectedIds }
      });
    }
  }, [
    changeLifestyleTypesFiltersSelected,
    lifestyleTypesSelectedFilters?.id?.in,
    selectedIds
  ]);

  const handleLifestyleTypesFilterSearch = useCallback(
    (input: string) => {
      changeLifestyleTypesFilters(
        { name: { ilike: input } },
        input ? [] : ['name']
      );
    },
    [changeLifestyleTypesFilters]
  );

  const lifestyleTypesFilterSelectedData = useMemo(() => {
    const selectedFetchedData = lifestyleTypesSelected.map((category) => ({
      id: category.id as string,
      label: category.name
    }));

    const selectedData = compact(
      selectedIds?.map((id) => find(selectedFetchedData, { id }))
    );
    return selectedData;
  }, [lifestyleTypesSelected, selectedIds]);

  const lifestylesTypesFilterData = useMemo<LifestyleTypesDataItem[]>(() => {
    return sortBy(
      lifestyleTypes?.map((category) => ({
        id: category.id as string,
        label: category.name
      })),
      'label'
    );
  }, [lifestyleTypes]);

  const handleChange = useCallback<
    (
      changedFilters: { [name: string]: string[] | undefined },
      removeFilters: string[]
    ) => void
  >(
    (changedFilters, removeFilters) => {
      changeLifestylesFilters(
        { [name]: { in: changedFilters?.[name] || [] } },
        removeFilters
      );
    },
    [changeLifestylesFilters, name]
  );

  return {
    allLifestylesTypesFilterData: uniqBy(
      compact(
        concat(lifestyleTypesFilterSelectedData, lifestylesTypesFilterData)
      ),
      'id'
    ),
    lifestyleTypesFilterSelectedData,
    lifestylesTypesFilterData,
    lifestyleTypesFilterErrorMessage: lifestyleTypesErrorMessage,
    lifestylesTypesFilterSelectedErrorMessage:
      lifestylesTypesSelectedErrorMessage,
    lifestyleTypesFilterFetched: lifestyleTypesFetched,
    lifestyleTypesFilterIsPlaceholderData: lifestyleTypesIsPlaceholderData,
    lifestyleTypesFilterFetchingNextPage: lifestyleTypesFetchingNextPage,
    hasNextLifestyleTypesFilterPage: hasNextLifestyleTypesPage,
    lifestyleTypesFilterSearchValue,
    handleLifestyleTypesFilterSearch,
    loadMoreLifestyleTypesFilter: loadMoreLifestyleTypes,
    handleChangeLifestylesTypesFilter: handleChange
  };
}

export default useLifestylesTypesFilter;
